import type { DateTime } from "luxon";
import { createContext, useContext } from "react";
import type { ResolvedActualDay } from "../../components/StaffTable/utils";
import { EMPTY_PUNCH_SUMMARY, type PunchSummary } from "../../utils/models";

const EMPTY_RESOLVED_ACTUAL_DAY: ResolvedActualDay = { actualShifts: [], substitutedShifts: [], missingFromScheduled: [] };

export type WeekPunchSummaryContextType = {
	punchSummariesByDate: Map<string, PunchSummary>;
	resolvedActualByDate: Map<string, ResolvedActualDay>;
};

export const WeekPunchSummaryContext = createContext<WeekPunchSummaryContextType | null>(null);

const useWeekPunchSummaryContext = (): WeekPunchSummaryContextType => {
	const context = useContext(WeekPunchSummaryContext);
	if (!context) throw new Error("useWeekPunchSummary must be used within a WeekViewProvider");
	return context;
};

export const useWeekPunchSummary = (date: DateTime): PunchSummary => {
	const { punchSummariesByDate } = useWeekPunchSummaryContext();
	const iso = date.toISODate();
	return (iso && punchSummariesByDate.get(iso)) || EMPTY_PUNCH_SUMMARY;
};

export const useWeekPunchSummaries = (): Map<string, PunchSummary> => {
	return useWeekPunchSummaryContext().punchSummariesByDate;
};

/**
 * The day's actual-mode shifts, resolved once in `WeekViewProvider`. Read this rather than calling
 * `resolveActualDayShifts` again — the punch-summary buckets are built from this exact list, and
 * `ActualGroupedTableBody` matches the two by nurse id.
 */
/** Every loaded day's resolved actual-mode shifts, by ISO date — `useResolvedActualDay` for a whole week at once. */
export const useResolvedActualByDate = (): Map<string, ResolvedActualDay> => useWeekPunchSummaryContext().resolvedActualByDate;

export const useResolvedActualDay = (date: DateTime): ResolvedActualDay => {
	const { resolvedActualByDate } = useWeekPunchSummaryContext();
	const iso = date.toISODate();
	return (iso && resolvedActualByDate.get(iso)) || EMPTY_RESOLVED_ACTUAL_DAY;
};
